/** Salary payment endpoints (`/salaries`, management only). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getData, getList, postData } from "../lib/apiClient";
import { queryKeys } from "./queryKeys";
import {
  SalaryPayment,
  SalaryStatus,
  SalarySummary,
  SalaryTransaction,
  SalaryTxnType,
  StaffLedgerSummary,
} from "../types/models";

export interface ListSalaryParams {
  page?: number;
  limit?: number;
  staffId?: string;
  periodMonth?: string; // YYYY-MM
  status?: SalaryStatus;
}

export interface RecordSalaryInput {
  staffId: string;
  type?: SalaryTxnType;
  periodMonth?: string; // YYYY-MM (required for a SALARY payment)
  amountPaid: number;
  amountDue?: number;
  paidAt?: string; // ISO date; defaults to now server-side
  note?: string;
}

export interface ListTransactionsParams {
  page?: number;
  limit?: number;
  staffId?: string;
  type?: SalaryTxnType;
  dateFrom?: string;
  dateTo?: string;
}

export const salariesApi = {
  list: (params?: ListSalaryParams) => getList<SalaryPayment>("/salaries", params),
  summary: (periodMonth?: string) =>
    getData<SalarySummary>("/salaries/summary", { periodMonth }),
  record: (input: RecordSalaryInput) =>
    postData<SalaryTransaction>("/salaries", input),
  transactions: (params?: ListTransactionsParams) =>
    getList<SalaryTransaction>("/salaries/transactions", params),
  ledgerSummary: (staffId: string) =>
    getData<StaffLedgerSummary>("/salaries/transactions/summary", { staffId }),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useSalaries = (params?: ListSalaryParams) =>
  useQuery({
    queryKey: queryKeys.salaries.list(params),
    queryFn: () => salariesApi.list(params),
  });

export const useSalarySummary = (periodMonth?: string) =>
  useQuery({
    queryKey: queryKeys.salaries.summary(periodMonth),
    queryFn: () => salariesApi.summary(periodMonth),
  });

export const useSalaryTransactions = (params: ListTransactionsParams, enabled = true) =>
  useQuery({
    queryKey: queryKeys.salaries.transactions(params),
    queryFn: () => salariesApi.transactions(params),
    enabled,
    placeholderData: (prev) => prev,
  });

export const useStaffLedgerSummary = (staffId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.salaries.ledgerSummary(staffId),
    queryFn: () => salariesApi.ledgerSummary(staffId as string),
    enabled: Boolean(staffId),
  });

export const useRecordSalary = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordSalaryInput) => salariesApi.record(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.salaries.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.overview });
    },
  });
};
